// Browser-compatible JWT token service

export interface UserClaims {
  email: string;
  userId: string;
  isAuthenticated: string;
  permissions: PermissionClaim[];
}

export interface PermissionClaim {
  screenName: string;
  action: "Read" | "Create" | "Update" | "Delete";
}

export interface ScreenPermissions {
  screenName: string;
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface DecodedTokenPayload {
  email?: string;
  nameid?: string; // ClaimTypes.NameIdentifier maps to 'nameid'
  IsAuthenticated?: string;
  permission?: string | string[]; // Can be single permission or array
  // Full claim URIs
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"?: string;
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"?: string;
  iss?: string; // Issuer
  aud?: string; // Audience
  exp?: number; // Expiration
  iat?: number; // Issued at
  [key: string]: string | string[] | number | boolean | undefined; // Allow for additional claims
}

export interface UserInfo {
  userId: string;
  email: string;
  isAuthenticated: boolean;
  permissions: ScreenPermissions[];
}

export class TokenService {
  /**
   * Decode JWT token using browser-native base64 decoding
   */
  static decodeToken(token: string): DecodedTokenPayload | null {
    try {
      // JWT structure: header.payload.signature
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid JWT format");
      }

      // Decode the payload (second part)
      const payload = parts[1];

      // Add padding if needed for base64 decoding
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);

      // Decode base64
      const decodedPayload = atob(
        paddedPayload.replace(/-/g, "+").replace(/_/g, "/")
      );

      // Parse JSON
      const decoded = JSON.parse(decodedPayload) as DecodedTokenPayload;
      return decoded;
    } catch (error) {
      console.error("Failed to decode JWT token:", error);
      return null;
    }
  }

  /**
   * Extract claim value, trying both short and full URI formats
   */
  private static extractClaim(
    decoded: DecodedTokenPayload,
    shortName: string,
    fullUri: string
  ): string {
    return (decoded[shortName] as string) || (decoded[fullUri] as string) || "";
  }

  /**
   * Debug method to log all claims in a token (useful for development)
   */

  /**
   * Extract user information from JWT token
   */
  static getUserInfoFromToken(token: string): UserInfo | null {
    const decoded = this.decodeToken(token);

    if (!decoded) {
      return null;
    }

    try {
      // Extract email from either short claim or full URI claim
      const email = this.extractClaim(
        decoded,
        "email",
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
      );

      // Extract user ID from either short claim or full URI claim
      const userId = this.extractClaim(
        decoded,
        "nameid",
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
      );

      const userInfo: UserInfo = {
        userId: userId,
        email: email,
        isAuthenticated: decoded.IsAuthenticated === "true",
        permissions: this.extractPermissions(decoded.permission),
      };

      return userInfo;
    } catch (error) {
      console.error("Failed to extract user info from token:", error);
      return null;
    }
  }

  /**
   * Extract and organize permissions from token claims
   */
  private static extractPermissions(
    permissionClaims?: string | string[]
  ): ScreenPermissions[] {
    if (!permissionClaims) {
      return [];
    }

    // Ensure we have an array of permissions
    const permissions = Array.isArray(permissionClaims)
      ? permissionClaims
      : [permissionClaims];

    // Group permissions by screen name
    const screenPermissionsMap = new Map<string, ScreenPermissions>();

    permissions.forEach((permission) => {
      // Permission format: "ScreenName:Action" (e.g., "Dashboard:Read", "User:Create")
      const [screenName, action] = permission.split(":");

      if (!screenName || !action) {
        return; // Skip invalid permission format
      }

      if (!screenPermissionsMap.has(screenName)) {
        screenPermissionsMap.set(screenName, {
          screenName,
          read: false,
          create: false,
          update: false,
          delete: false,
        });
      }

      const screenPermission = screenPermissionsMap.get(screenName)!;

      switch (action.toLowerCase()) {
        case "read":
          screenPermission.read = true;
          break;
        case "create":
          screenPermission.create = true;
          break;
        case "update":
          screenPermission.update = true;
          break;
        case "delete":
          screenPermission.delete = true;
          break;
      }
    });

    return Array.from(screenPermissionsMap.values());
  }

  /**
   * Check if user has specific permission for a screen
   */
  static hasPermission(
    token: string,
    screenName: string,
    action: "read" | "create" | "update" | "delete"
  ): boolean {
    const userInfo = this.getUserInfoFromToken(token);

    if (!userInfo || !userInfo.isAuthenticated) {
      return false;
    }

    const screenPermission = userInfo.permissions.find(
      (p) => p.screenName.toLowerCase() === screenName.toLowerCase()
    );

    if (!screenPermission) {
      return false;
    }

    return screenPermission[action];
  }

  /**
   * Get all permissions for a specific screen
   */
  static getScreenPermissions(
    token: string,
    screenName: string
  ): ScreenPermissions | null {
    const userInfo = this.getUserInfoFromToken(token);

    if (!userInfo) {
      return null;
    }

    return (
      userInfo.permissions.find(
        (p) => p.screenName.toLowerCase() === screenName.toLowerCase()
      ) || null
    );
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);

    if (!decoded || !decoded.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  }

  /**
   * Get token expiration date
   */
  static getTokenExpiration(token: string): Date | null {
    const decoded = this.decodeToken(token);

    if (!decoded || !decoded.exp) {
      return null;
    }

    return new Date(decoded.exp * 1000);
  }

  /**
   * Validate token structure and required claims
   */
  static isValidToken(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);

      if (!decoded) {
        return false;
      }

      // Check required claims
      const hasRequiredClaims = Boolean(
        decoded.email && decoded.IsAuthenticated !== undefined
      );

      // Check if token is not expired
      const isNotExpired = !this.isTokenExpired(token);

      return hasRequiredClaims && isNotExpired;
    } catch {
      return false;
    }
  }

  /**
   * Get user email from token
   */
  static getUserEmail(token: string): string | null {
    const userInfo = this.getUserInfoFromToken(token);
    return userInfo?.email || null;
  }

  /**
   * Get user ID from token
   */
  static getUserId(token: string): string | null {
    const userInfo = this.getUserInfoFromToken(token);
    return userInfo?.userId || null;
  }

  /**
   * Get all screen names that user has access to
   */
  static getAccessibleScreens(token: string): string[] {
    const userInfo = this.getUserInfoFromToken(token);

    if (!userInfo) {
      return [];
    }

    return userInfo.permissions
      .filter((p) => p.read || p.create || p.update || p.delete)
      .map((p) => p.screenName);
  }

  /**
   * Store token in localStorage (optional helper method)
   */
  static storeToken(token: string): void {
    if (this.isValidToken(token)) {
      localStorage.setItem("bestekJwtToken", token);
    } else {
      throw new Error("Invalid token cannot be stored");
    }
  }

  /**
   * Retrieve token from localStorage (optional helper method)
   */
  static getStoredToken(): string | null {
    const token = localStorage.getItem("bestekJwtToken");

    if (!token) {
      return null;
    }

    if (this.isValidToken(token)) {
      return token;
    } else {
      // Remove invalid token
      localStorage.removeItem("bestekJwtToken");
      return null;
    }
  }

  /**
   * Remove token from storage
   */
  static clearToken(): void {
    localStorage.removeItem("bestekJwtToken");
    sessionStorage.removeItem("bestekJwtToken");
  }
}
